import Icon from '@chegg-wt/core/components/Icon'
import React, { useRef, useState } from 'react'
import { citationHasValidSourceLink } from '@chegg-wt/core/utils/citations'
import {
  origins,
  UserClickPayload,
} from '@chegg-wt/core/state/instrumentation/rio/clickstreamInteractions'
import { useOnClickOutside } from '@chegg-wt/core/hooks/useOnClickOutside'
import { Citation } from '@chegg-wt/core/types/redux-citation-api'
import * as Styled from './styled'

export const getMenuId = (citationId: string) => `more-menu-${citationId}`
interface Props {
  citation: Citation
  isFootnote: boolean
  openNotes?: (showNotes: boolean) => void
  editCitation: (citation: Citation) => void
  deleteCitations: (citations: Citation[]) => void
  copyCitations: (citations: Citation[]) => void
  copyPremiumCitations?: () => void
  userClick: (payload: UserClickPayload) => void
}

const Menu: React.FC<Props> = ({
  citation,
  isFootnote,
  openNotes,
  editCitation,
  deleteCitations,
  copyCitations,
  copyPremiumCitations,
  userClick,
}) => {
  const [showMenu, toggleMenu] = useState(false)
  const isSourceVisitable = citationHasValidSourceLink(citation)
  const closeMenu = () => toggleMenu(false)

  // Refs for focus management
  const dropdown = useRef<HTMLDivElement>(null)
  const menuTriggerRef = useRef<HTMLButtonElement>(null)
  const optionsRef = useRef<(HTMLButtonElement | HTMLAnchorElement)[]>([])

  useOnClickOutside(dropdown, closeMenu)

  // Handle keyboard events
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!showMenu) return

    const focusableElements = optionsRef.current.filter((el) => el !== null)
    const currentIndex = focusableElements.indexOf(
      document.activeElement as HTMLButtonElement | HTMLAnchorElement
    )
    const nextIndex = e.shiftKey
      ? (currentIndex - 1 + focusableElements.length) % focusableElements.length
      : (currentIndex + 1) % focusableElements.length

    switch (e.key) {
      case 'Escape':
        e.preventDefault()
        closeMenu()
        menuTriggerRef.current?.focus()
        break
      case 'Tab':
        e.preventDefault()
        focusableElements[nextIndex]?.focus()
        break
      default:
        break
    }
  }

  const menuText = isFootnote ? 'footnote' : 'in-text citation'
  const handleTrigger = () => {
    closeMenu()
    toggleMenu(!showMenu)
  }

  const handleCopy = () => {
    closeMenu()
    copyCitations([citation])
    userClick({ origin: origins.copyBibliographyCitationFromMenu })
  }

  const handlePremiumCopy = () => {
    closeMenu()
    copyPremiumCitations()
  }

  const handleEdit = () => {
    closeMenu()
    editCitation(citation)
    userClick({ origin: origins.editCitationClick })
  }

  const handleDelete = () => {
    closeMenu()
    deleteCitations([citation])
    userClick({ origin: origins.deleteCitationClick })
  }

  const handleNotes = () => {
    closeMenu()
    openNotes(true)
    userClick({ origin: origins.addNoteClick })
  }

  const handleVisitSource = () => {
    closeMenu()
    userClick({ origin: origins.visitSourceLinkClick })
  }

  return (
    <Styled.Container onKeyDown={handleKeyDown}>
      <Styled.MenuTrigger
        ref={menuTriggerRef}
        id={getMenuId(citation.id)}
        data-test="citations-more-menu"
        onClick={handleTrigger}
        aria-label="Show citation actions menu"
        aria-expanded={showMenu}
        aria-haspopup="true"
        aria-controls="dropdown-menu"
      >
        <Icon name="menu" title={`icon-${getMenuId(citation.id)}`} />
      </Styled.MenuTrigger>
      {showMenu && (
        <Styled.OptionsContainer
          id="dropdown-menu"
          ref={dropdown}
          role="listbox"
          aria-label="Citation Actions"
          tabIndex={-1}
        >
          <Styled.Option
            ref={(el) => {
              optionsRef.current[0] = el
            }}
            role="option"
            data-test="dropdown-copy-citation"
            onClick={handleCopy}
          >
            Copy citation
          </Styled.Option>
          <Styled.Option
            ref={(el) => {
              optionsRef.current[1] = el
            }}
            role="option"
            data-test="dropdown-edit-citation"
            onClick={handleEdit}
          >
            Edit citation
          </Styled.Option>
          <Styled.Option
            ref={(el) => {
              optionsRef.current[2] = el
            }}
            role="option"
            data-test="dropdown-delete-citation"
            onClick={handleDelete}
            red
          >
            Delete citation
          </Styled.Option>
          {openNotes && (
            <Styled.Option
              ref={(el) => {
                optionsRef.current[3] = el
              }}
              role="option"
              aria-label="Show citation notes"
              data-test="dropdown-notes"
              onClick={handleNotes}
            >
              Notes
            </Styled.Option>
          )}
          {copyPremiumCitations && (
            <Styled.Option
              ref={(el) => {
                optionsRef.current[4] = el
              }}
              role="option"
              data-test="dropdown-premium-copy"
              onClick={handlePremiumCopy}
            >
              Copy {menuText}
            </Styled.Option>
          )}
          {isSourceVisitable && (
            <Styled.Option
              ref={(el) => {
                optionsRef.current[5] = el
              }}
              as="a"
              href={citation.pubonline.url}
              target="_blank"
              role="option"
              data-test="dropdown-visit-source-link"
              onClick={handleVisitSource}
              tabIndex={0}
            >
              Visit source link
            </Styled.Option>
          )}
        </Styled.OptionsContainer>
      )}
    </Styled.Container>
  )
}

export default Menu
